import * as binderOrderItemTypes from "../enums/binderOrderItemTypes";
import * as reduxKeyHelpers from "./reduxKeyHelpers";
import find from "lodash.find";
import findIndex from "lodash.findindex";

export function insert(orderItems, targetId, newItem, insertBefore = false) {
	if (!orderItems || !orderItems.length) {
		return orderItems;
	}

	const foundIndex = findIndex(orderItems, i => targetId === i.id);
	if (foundIndex !== -1) {
		const splitIndex = insertBefore ? foundIndex : foundIndex + 1;
		return [...orderItems.slice(0, splitIndex), newItem, ...orderItems.slice(splitIndex)];
	}

	return orderItems.map(i => ({
		...i,
		children: insert(i.children, targetId, newItem, insertBefore),
	}));
}

export function insertChild(orderItems, parentId, newItem) {
	if (!orderItems || !orderItems.length) {
		return orderItems;
	}

	const foundIndex = findIndex(orderItems, i => parentId === i.id);
	if (foundIndex !== -1) {
		return [
			...orderItems.slice(0, foundIndex),
			{
				...orderItems[foundIndex],
				children: [...orderItems[foundIndex].children, newItem],
			},
			...orderItems.slice(foundIndex + 1),
		];
	}

	return orderItems.map(i => ({
		...i,
		children: insertChild(i.children, parentId, newItem),
	}));
}

export function remove(orderItems, id) {
	if (!orderItems || !orderItems.length) {
		return orderItems;
	}

	const foundIndex = findIndex(orderItems, i => id === i.id);
	if (foundIndex !== -1) {
		return [...orderItems.slice(0, foundIndex), ...orderItems.slice(foundIndex + 1)];
	}

	return orderItems.map(i => ({
		...i,
		children: remove(i.children, id),
	}));
}

export function modify(orderItems, id, transform) {
	if (!orderItems || !orderItems.length) {
		return orderItems;
	}

	const foundIndex = findIndex(orderItems, i => id === i.id);
	if (foundIndex !== -1) {
		return [
			...orderItems.slice(0, foundIndex),
			transform(orderItems[foundIndex]),
			...orderItems.slice(foundIndex + 1),
		];
	}

	return orderItems.map(i => ({
		...i,
		children: modify(i.children, id, transform),
	}));
}

const orderItemWithFinalId = (item, orderedTabIds, binderId) => {
	if (!item.children || !item.children.length) {
		const id =
			item.type === binderOrderItemTypes.TAB
				? findIndex(
						orderedTabIds,
						tid => tid === reduxKeyHelpers.getKeyFromBinderId(binderId, item.id),
				  )
				: item.id;
		return {
			...item,
			children: [],
			id,
		};
	}

	return {
		...item,
		children: item.children.map(child => orderItemWithFinalId(child, orderedTabIds, binderId)),
	};
};

export function getSavedTabIds(orderItems, orderedTabIds, binderId) {
	return orderItems.map(i => orderItemWithFinalId(i, orderedTabIds, binderId));
}

export function createTabOrderItem(tabId, tabTitle) {
	return {
		id: tabId,
		type: binderOrderItemTypes.TAB,
		label: tabTitle,
		children: [],
	};
}

export function getFullLabel(item) {
	if (item.type === binderOrderItemTypes.FOLDER) {
		const itemLabel = item.label;
		const tabCount = item.children
			? item.children.filter(child => child.type === binderOrderItemTypes.TAB).length
			: 0;
		return tabCount > 0 ? `${itemLabel} (${tabCount})` : itemLabel;
	}
	return item.label;
}

export const getLabelChildrenCount = obj => {
	if (!obj || !obj.children || !Array.isArray(obj.children)) {
		return { count: 0, label: null }; // Return 0 count and null label if object or children property is invalid
	}

	const countDirectChildren = obj.children.length;

	const countNestedChildren = obj.children.reduce((count, child) => {
		if (child.children && Array.isArray(child.children)) {
			return count + getLabelChildrenCount(child).count;
		}
		return count;
	}, 0);

	const allChildrenCount = countDirectChildren + countNestedChildren;
	const label = allChildrenCount ? `${obj.label} (${allChildrenCount})` : obj.label;

	return { count: countDirectChildren + countNestedChildren, label: label };
};

function getPath(orderItems, itemSearch) {
	const path = [];

	const foundIndex = findIndex(orderItems, itemSearch);
	if (foundIndex !== -1) {
		path.push(foundIndex);
		return path;
	}

	for (let i = 0; i < orderItems.length; i++) {
		const item = orderItems[i];
		foundIndex;
		const subpath = getPath(item.children, itemSearch);
		if (subpath && subpath.length) {
			path.push(i, ...subpath);
			return path;
		}
	}

	return path;
}

export function getPathItems(orderItems, itemSearch) {
	const path = getPath(orderItems, itemSearch);

	const pathItems = [];

	let items = orderItems;
	path.forEach(index => {
		const item = items[index];
		pathItems.push(item);
		items = item.children;
	});

	return pathItems;
}

export function isValidDrop(orderItems, sourceItem, targetId) {
	if (!sourceItem || targetId === null || targetId === undefined || sourceItem.id === targetId) {
		return false;
	}

	if (sourceItem.type === binderOrderItemTypes.FOLDER) {
		const targetPathItems = getPathItems(orderItems, item => item.id === targetId);
		return !find(targetPathItems, item => item.id === sourceItem.id);
	}

	return true;
}

export function bfs(orderItem, search) {
	let found;
	let queue = [orderItem];
	while (queue.length) {
		const current = queue[0];
		queue = queue.slice(1);
		if (search(current)) {
			found = current;
			break;
		}
		if (current.children) {
			queue = [...queue, ...current.children];
		}
	}
	return found;
}
